import React, { useMemo } from "react";
import { MealPlannerTranslations } from "../ecosystems/mealplans/mealplan/components/MealPlanner/translations";
import { defaultViewSearchTags } from "../ecosystems/mealplans/mealplan/components/RecipeSelector/defaultViewTags";
import { MealType } from "../types/graphql-global-types";
import { MealPlanner_GetTagsQuery_listTags_tags } from "../types/MealPlanner_GetTagsQuery";

const notInterestingTags = ["sidedish-high-protein"];

interface TagInterface {
  __typename: "Tag";
  id: string;
  title: string;
  type: string;
}

export function useTags(allTags: Array<TagInterface>, tt: MealPlannerTranslations, currentMeal?: MealType) {
  const translateTag = useTagTranslation(tt);

  const tags = useMemo(() => {
    const strictnessTags = allTags.filter((tag) => tag.type === "energy").map(translateTag);
    const difficultyTags = allTags.filter((tag) => tag.type === "ease").map(translateTag);
    const typeTags = allTags.filter((tag) => tag.type === "recipe_type").map(translateTag);
    const proteinTags = allTags.filter((tag) => tag.type === "recipe_content").map(translateTag);
    const dishTags = allTags.filter((tag) => tag.type === "dish_type").map(translateTag);
    const ingredientTags = allTags.filter((tag) => tag.type === "ingredient_type").map(translateTag);
    const dietTags = allTags
      .filter((tag) => tag.type === "recipe_diet")
      .map(translateTag)
      .filter((tag) => !notInterestingTags.includes(tag.title));
    return [
      { title: tt.recipeSelectorDialog.tags.type, tags: typeTags },
      { title: tt.recipeSelectorDialog.tags.protein, tags: proteinTags },
      { title: tt.recipeSelectorDialog.tags.diet, tags: dietTags },
      { title: tt.recipeSelectorDialog.tags.strictness, tags: strictnessTags },
      { title: tt.recipeSelectorDialog.tags.difficulty, tags: difficultyTags },
      { title: tt.recipeSelectorDialog.tags.ingredient, tags: ingredientTags },
      { title: tt.recipeSelectorDialog.tags.dish, tags: dishTags },
    ];
  }, [allTags, tt, translateTag]);

  const tagsWithId = useMemo(() => {
    const tagsWithoutId =
      currentMeal === MealType.BREAKFAST
        ? defaultViewSearchTags.breakfast
        : currentMeal === MealType.LUNCH
        ? defaultViewSearchTags.lunch
        : defaultViewSearchTags.dinner;

    const recipeTags = allTags.filter(
      (tag) =>
        tag.type === "recipe_content" ||
        tag.type === "recipe_diet" ||
        tag.type === "energy" ||
        tag.type === "dish_type" ||
        tag.type === "ease"
    );

    return recipeTags
      .filter((tagWithId) => tagsWithoutId.some((tagWithoutId) => tagWithoutId.ids.includes(tagWithId.id)))
      .map(translateTag);
  }, [allTags, currentMeal, translateTag]);

  return { tags, tagsWithId };
}

function useTagTranslation(tt: MealPlannerTranslations) {
  const translations: Partial<Record<string, string>> = React.useMemo(() => {
    return {
      "wp-es-tag-1": tt.recipeSelectorDialog.tags.keto,
      "wp-se-tag-1": tt.recipeSelectorDialog.tags.keto,

      "wp-es-tag-2": tt.recipeSelectorDialog.tags.moderate,
      "wp-se-tag-2": tt.recipeSelectorDialog.tags.moderate,

      "wp-es-tag-3": tt.recipeSelectorDialog.tags.liberal,
      "wp-se-tag-3": tt.recipeSelectorDialog.tags.liberal,

      "wp-es-tag-4": tt.recipeSelectorDialog.tags.beginner,
      "wp-se-tag-4": tt.recipeSelectorDialog.tags.beginner,

      "wp-es-tag-5": tt.recipeSelectorDialog.tags.easy,
      "wp-se-tag-5": tt.recipeSelectorDialog.tags.easy,

      "wp-es-tag-6": tt.recipeSelectorDialog.tags.medium,
      "wp-se-tag-6": tt.recipeSelectorDialog.tags.medium,

      "wp-es-tag-7": tt.recipeSelectorDialog.tags.hard,
      "wp-se-tag-7": tt.recipeSelectorDialog.tags.hard,

      "wp-es-tag-1033": tt.recipeSelectorDialog.tags.highProtein,
      "wp-se-tag-1106": tt.recipeSelectorDialog.tags.highProtein,

      "wp-es-tag-1053": tt.recipeSelectorDialog.tags.highProteinMainCourse,
      "wp-se-tag-1144": tt.recipeSelectorDialog.tags.highProteinMainCourse,

      "wp-es-tag-1054": tt.recipeSelectorDialog.tags.highProteinMeal,
      "wp-se-tag-1145": tt.recipeSelectorDialog.tags.highProteinMeal,

      "wp-es-tag-1055": tt.recipeSelectorDialog.tags.highProteinDinner,
      "wp-se-tag-1146": tt.recipeSelectorDialog.tags.highProteinDinner,

      "wp-es-tag-1056": tt.recipeSelectorDialog.tags.highProteinLunch,
      "wp-se-tag-1147": tt.recipeSelectorDialog.tags.highProteinLunch,

      "wp-es-tag-1057": tt.recipeSelectorDialog.tags.highProteinBreakfast,
      "wp-se-tag-1148": tt.recipeSelectorDialog.tags.highProteinBreakfast,

      "wp-es-tag-1058": tt.recipeSelectorDialog.tags.highProteinSnack,
      "wp-se-tag-1149": tt.recipeSelectorDialog.tags.highProteinSnack,

      "wp-es-tag-1059": tt.recipeSelectorDialog.tags.highProteinDessert,
      "wp-se-tag-1150": tt.recipeSelectorDialog.tags.highProteinDessert,

      "wp-es-tag-1060": tt.recipeSelectorDialog.tags.highProteinBread,
      "wp-se-tag-1151": tt.recipeSelectorDialog.tags.highProteinBread,

      "wp-es-tag-1061": tt.recipeSelectorDialog.tags.highProteinAppetizer,
      "wp-se-tag-1152": tt.recipeSelectorDialog.tags.highProteinAppetizer,

      "wp-es-tag-972": tt.recipeSelectorDialog.tags.soy,
      "wp-se-tag-1076": tt.recipeSelectorDialog.tags.soy,

      "wp-es-tag-973": tt.recipeSelectorDialog.tags.nuts,
      "wp-se-tag-1077": tt.recipeSelectorDialog.tags.nuts,

      "wp-es-tag-974": tt.recipeSelectorDialog.tags.dairy,
      "wp-se-tag-1078": tt.recipeSelectorDialog.tags.dairy,

      "wp-es-tag-975": tt.recipeSelectorDialog.tags.egg,
      "wp-se-tag-1079": tt.recipeSelectorDialog.tags.egg,

      "wp-es-tag-976": tt.recipeSelectorDialog.tags.fish,
      "wp-se-tag-1080": tt.recipeSelectorDialog.tags.fish,

      "wp-es-tag-977": tt.recipeSelectorDialog.tags.meat,
      "wp-se-tag-1081": tt.recipeSelectorDialog.tags.meat,

      "wp-es-tag-978": tt.recipeSelectorDialog.tags.beef,
      "wp-se-tag-1082": tt.recipeSelectorDialog.tags.beef,

      "wp-es-tag-979": tt.recipeSelectorDialog.tags.game,
      "wp-se-tag-1083": tt.recipeSelectorDialog.tags.game,

      "wp-es-tag-980": tt.recipeSelectorDialog.tags.lamb,
      "wp-se-tag-1084": tt.recipeSelectorDialog.tags.lamb,

      "wp-es-tag-981": tt.recipeSelectorDialog.tags.pork,
      "wp-se-tag-1085": tt.recipeSelectorDialog.tags.pork,

      "wp-es-tag-982": tt.recipeSelectorDialog.tags.poultry,
      "wp-se-tag-1086": tt.recipeSelectorDialog.tags.poultry,

      "wp-es-tag-983": tt.recipeSelectorDialog.tags.chicken,
      "wp-se-tag-1087": tt.recipeSelectorDialog.tags.chicken,

      "wp-es-tag-984": tt.recipeSelectorDialog.tags.turkey,
      "wp-se-tag-1088": tt.recipeSelectorDialog.tags.turkey,

      "wp-es-tag-985": tt.recipeSelectorDialog.tags.duck,
      "wp-se-tag-1089": tt.recipeSelectorDialog.tags.duck,

      "wp-es-tag-986": tt.recipeSelectorDialog.tags.seafood,
      "wp-se-tag-1090": tt.recipeSelectorDialog.tags.seafood,
    };
  }, [tt]);

  const fn = React.useCallback(
    (tag: MealPlanner_GetTagsQuery_listTags_tags) => {
      const title = translations[tag.id];
      if (title) {
        return {
          ...tag,
          title,
        };
      } else {
        return tag;
      }
    },
    [translations]
  );

  return fn;
}
